import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Paper, Stack, Typography, Button, Box, Avatar, styled } from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useOnboardingCallBanner, { CallType, ONBOARDING_CALL_QUERY_KEY } from './hooks/useOnboardingCallBanner';
import useHasTimeArrived from './hooks/useHasTimeArrived';

import CalendlyDialogSSC from 'modules/common/components/CalendlyDialog/CalendlyDialogSSC';
import HeadsetThinIcon from 'modules/common/components/Icons/HeadsetThinIcon';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const JOIN_OPENS_MINUTES_BEFORE = 10;

interface DialogContext {
    callType: CallType | null;
    slug: string;
    url: string;
    isReschedule: boolean;
}

// Figma MasterButton, corner-full. The theme's Button has no pill shape yet.
const PillButton = styled(Button)({
    borderRadius: 999,
});

/**
 * Banner for scheduling EM/OA onboarding calls
 * Shows different content based on whether a call is scheduled
 */
export default function OnboardingCallBanner() {
    const queryClient = useQueryClient();
    const { sendTrackingEvent } = useEventTracking();

    const {
        shouldShow,
        callType,
        calendlyUrl,
        member,
        isScheduled,
        scheduledTime,
        endTime,
        rescheduleUrl,
        joinUrl,
        slug,
        startPolling,
    } = useOnboardingCallBanner();

    const joinOpensAt = scheduledTime
        ? dayjs(scheduledTime).subtract(JOIN_OPENS_MINUTES_BEFORE, 'minute').valueOf()
        : null;
    const isJoinOpen = useHasTimeArrived(joinOpensAt);

    // Dialog state - captures context when opened to prevent stale values
    const [calendlyOpen, setCalendlyOpen] = useState(false);
    const [dialogContext, setDialogContext] = useState<DialogContext | null>(null);

    // Open dialog and capture current context
    const handleOpenCalendly = useCallback(() => {
        setDialogContext({
            callType,
            slug,
            url: isScheduled ? rescheduleUrl : calendlyUrl,
            isReschedule: isScheduled,
        });
        setCalendlyOpen(true);

        sendTrackingEvent(
            callType === CallType.EM ? 'EM Schedule Call Button Clicked' : 'OA Schedule Call Button Clicked',
            { category: 'onboarding_call_banner', action: 'button_clicked', callType, isScheduled }
        );
    }, [sendTrackingEvent, callType, slug, calendlyUrl, rescheduleUrl, isScheduled]);

    // Close dialog - start polling if it was a reschedule
    const handleCloseCalendly = useCallback(() => {
        setCalendlyOpen(false);
        if (dialogContext?.isReschedule) {
            startPolling(scheduledTime);
        }
        setDialogContext(null);
    }, [dialogContext, scheduledTime, startPolling]);

    // Handle successful scheduling - optimistic update + start polling
    const handleEventScheduled = useCallback(() => {
        setCalendlyOpen(false);

        const capturedCallType = dialogContext?.callType;
        const capturedSlug = dialogContext?.slug;
        if (!capturedCallType || !capturedSlug) {
            setDialogContext(null);
            return;
        }

        // Optimistic update: show "being confirmed" state while polling
        const queryKey: [string, string] = [ONBOARDING_CALL_QUERY_KEY, member?.email || ''];
        queryClient.setQueryData(queryKey, () => [
            {
                email: member?.email || '',
                slug: capturedSlug,
                scheduledtime: null,
                end_time: null,
                is_canceled: false,
                reschedule_url: '',
                location: '',
            },
        ]);

        sendTrackingEvent(capturedCallType === CallType.EM ? 'EM Call Scheduled' : 'OA Call Scheduled', {
            category: 'onboarding_call_banner',
            action: 'call_scheduled',
            callType: capturedCallType,
        });

        startPolling();
        setDialogContext(null);
    }, [queryClient, dialogContext, member?.email, sendTrackingEvent, startPolling]);

    const handleJoinCall = useCallback(() => {
        sendTrackingEvent(callType === CallType.EM ? 'EM Join Call Button Clicked' : 'OA Join Call Button Clicked', {
            category: 'onboarding_call_banner',
            action: 'join_clicked',
            callType,
        });
    }, [sendTrackingEvent, callType]);

    if (!shouldShow) {
        return null;
    }

    // Date formatting for scheduled state
    const formattedDay = scheduledTime ? dayjs(scheduledTime).format('dddd, MMM D') : '';
    const formattedStartTime = scheduledTime ? dayjs(scheduledTime).format('h:mma') : '';
    const formattedEndTime = endTime ? dayjs(endTime).format('h:mma') : '';
    const formattedTimezone = scheduledTime ? dayjs(scheduledTime).format('z') : '';
    // Shown as text, not a tooltip, so it reaches everyone while the disabled button can't take focus
    const joinOpensNote =
        joinUrl && !isJoinOpen ? ` · You can join ${JOIN_OPENS_MINUTES_BEFORE} minutes before it starts` : '';

    return (
        <>
            <Paper
                role="region"
                aria-label="Onboarding call banner"
                variant="outlined"
                sx={{
                    py: 1,
                    width: '100%',
                    maxWidth: '900px',
                    alignSelf: 'center',
                    backgroundColor: 'cement.100',
                    borderColor: 'transparent',
                }}>
                <Stack
                    direction="row"
                    spacing={3}
                    useFlexGap
                    sx={{ px: 3, py: 2, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <Avatar sx={{ width: 35, height: 35, backgroundColor: 'purple.100' }}>
                        <HeadsetThinIcon
                            sx={{ color: 'primary.main', fontSize: 'large', transform: 'translate(2px, 1px)' }}
                        />
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                        {isScheduled ? (
                            <>
                                <Typography variant="subtitle1" color="text.primary">
                                    {scheduledTime
                                        ? `You have a call scheduled for ${formattedDay}`
                                        : 'Your call is being confirmed'}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {scheduledTime
                                        ? `${formattedStartTime} - ${formattedEndTime} ${formattedTimezone}${joinOpensNote}`
                                        : 'Check back shortly for your scheduled time.'}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="subtitle1" color="text.primary">
                                    Need help completing your onboarding?
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Schedule a call with our team today to help walk you through onboarding.
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            ml: { xs: 0, sm: 2 },
                            flexShrink: 0,
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: 'flex-end',
                        }}>
                        {isScheduled && joinUrl && isJoinOpen && (
                            <Button
                                variant="contained"
                                color="primary"
                                href={joinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleJoinCall}>
                                Join Zoom call
                            </Button>
                        )}
                        {isScheduled && joinUrl && !isJoinOpen && (
                            <Button variant="contained" color="primary" disabled>
                                Join Zoom call
                            </Button>
                        )}
                        <PillButton variant="outlined" color="brandPurple" onClick={handleOpenCalendly}>
                            {isScheduled ? 'Reschedule call' : 'Schedule a call'}
                        </PillButton>
                    </Stack>
                </Stack>
            </Paper>

            {calendlyUrl && (
                <CalendlyDialogSSC
                    open={calendlyOpen}
                    onClose={handleCloseCalendly}
                    onEventScheduled={handleEventScheduled}
                    url={dialogContext?.url || calendlyUrl}
                    memberUuid={member?.uuid}
                    prefill={{
                        name: member?.fullname || member?.nickname || '',
                        email: member?.email || '',
                        company: member?.businessname || '',
                    }}
                    hideDetails={false}
                />
            )}
        </>
    );
}
